import React from "react";
import {
    Breadcrumb,
    IBreadcrumbItem,
    mergeStyleSets,
    nuOrderTheme as theme,
    Fabric,
    Theme,
    Icon,
    IBreadcrumbStyles,
    initializeIcons,
} from "@nuorder/components";
import { useFolderSelection } from "./use-folder-selection";
import { IFolderSelection } from "./folder-navigation";
import { ROOT_ITEM, SpaceView } from "containers/folders/hooks/utils";
import { useInitializeAssortmentFolderSelection } from "./assortment-folder-selection";
import { DATATEST } from "constants/integration";
import { formatMessage } from "lib/globalize-util";
import { DRIVE_LINK, MAX_LINEAGE_NODE } from "containers/folders/utils/constants";
import color from "color";
import me from "models/me";

interface IAssortmentBreadcrumbProps {
    item: nuorder.folder.v1.Item;
    merchantId: string;
    router: any;
}

initializeIcons();

export const AssortmentBreadcrumb: React.FC<IAssortmentBreadcrumbProps> = ({ item, merchantId, router }) => {
    const [settings, setSettings] = useFolderSelection();

    const handleBreadcrumbItemClicked = React.useCallback(
        (selection: IFolderSelection, view: SpaceView) =>
            (ev: React.MouseEvent<HTMLElement>, item: IBreadcrumbItem) => {
                if (!selection) {
                    router.push({
                        pathname: DRIVE_LINK,
                    });
                } else {
                    router.push({
                        pathname: DRIVE_LINK,
                        query: {
                            itemId: selection.isDrive ? ROOT_ITEM : item.key,
                            merchantId: me.isBrandUser() ? me.getLastMerchantId() : merchantId,
                            view,
                        },
                    });
                }
            },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const [breadcrumb, setBreadcrumb] = React.useState<IBreadcrumbItem[]>([
        {
            text: formatMessage("Assortments"),
            key: DRIVE_LINK,
            onClick: handleBreadcrumbItemClicked(null, SpaceView.MyDrive),
        },
    ]);

    useInitializeAssortmentFolderSelection(
        merchantId,
        item,
        (lineage: IFolderSelection[], view: SpaceView) => {
            setSettings({
                navigation: lineage,
                currentItemId: lineage[lineage.length - 1].id,
                currentView: view,
                rootItemId: lineage[0].id,
            });

            setBreadcrumb((items) => {
                const lastItem: IBreadcrumbItem = { text: "--", key: "last", as: "a" }; // added to display the separator at the end. This will be hidden
                if (lineage.length === 0 && items.length === 1 && items[0].key === DRIVE_LINK) {
                    return items.concat(lastItem);
                }

                return [
                    ...lineage.map<IBreadcrumbItem>((node) => ({
                        text: node.name,
                        key: node.id,
                        onClick: node.id === MAX_LINEAGE_NODE ? null : handleBreadcrumbItemClicked(node, view),
                        as: "a",
                    })),
                    lastItem,
                ];
            });
        },
        true
    );

    const classes = React.useMemo(
        () =>
            mergeStyleSets({
                truncation: {
                    display: "flex",
                    alignItems: "center",

                    svg: {
                        fill: theme.palette.greyDark,
                    },
                },
                chevron: {
                    margin: "0 2px",
                    display: "flex",
                    alignItems: "center",

                    svg: {
                        fill: theme.palette.greyDark,
                    },
                },
            }),
        []
    );

    const styles = React.useMemo<Partial<IBreadcrumbStyles>>(
        () => ({
            root: {
                margin: 0,
                marginLeft: 8,

                "& a": {
                    color: color(theme.palette.white).alpha(0.8).rgbString(),
                },
            },
            list: {
                "& li:last-child": {
                    display: "none",
                },
            },
            itemLink: {
                fontSize: 11,
                lineHeight: "16px",
                letterSpacing: "0.2px",
                margin: "0 !important",
                borderBottom: 0,
                color: color(theme.palette.white).alpha(0.8).rgbString(),
                padding: 0,
                textDecoration: "none !important",

                "&:hover, &:active, &:focus": {
                    background: "none !important",
                    textDecoration: "underline !important",
                    color: `${theme.palette.white} !important`,
                },
            },
        }),
        []
    );

    const handleRenderOverflowIcon = React.useCallback(
        () => <Icon iconName="ellipsis14" className={classes.truncation} />,
        [classes]
    );

    const handleRenderDivider = React.useCallback(
        () => <Icon iconName="chevronRight14" className={classes.chevron} />,
        [classes]
    );

    return (
        <Theme>
            <Fabric>
                <Breadcrumb
                    data-test={DATATEST.FOLDERS.BREADCRUMB}
                    items={breadcrumb}
                    maxDisplayedItems={4} // one more due to the last item added in order to display the separator
                    overflowIndex={breadcrumb.length > 1 ? 1 : 0}
                    styles={styles}
                    dividerAs={handleRenderDivider}
                    onRenderOverflowIcon={handleRenderOverflowIcon}
                />
            </Fabric>
        </Theme>
    );
};
